import React, { useState, useEffect, useCallback } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { getTranslation } from './translations';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import './BannerCarousel.css';

const ACCENT_COLORS = ['#3EA6FF', '#00838F', '#2BA640', '#E65100', '#9C27B0', '#FF0000'];

const DEFAULT_PROMO_CARDS = [
  {
    id: 1,
    badgeKey: 'featured',
    badge: 'Featured',
    tagline: 'SpeedoTube Original',
    title: 'A Jaw-Dropping Video Experience!',
    description: 'We’re Bringing Our Passion For Ultimate Customer Satisfaction With The Best Quality Videos.',
    ctaKey: 'streamNow',
    cta: 'Watch Now',
    accentColor: '#3EA6FF',
    bgImage: (process.env.PUBLIC_URL || '') + '/img/banner.png',
    link: '',
  },
  {
    id: 2,
    badgeKey: 'featured',
    badge: 'Featured',
    tagline: 'Speedo TV',
    title: 'Watch Award-Winning Documentaries & Live Streams in 60 FPS',
    description: 'Stream thousands of exclusive live broadcasts, sports events, and cinema classics.',
    ctaKey: 'streamNow',
    cta: 'Stream Now',
    accentColor: '#00838F',
    bgImage: (process.env.PUBLIC_URL || '') + '/img/ad_banner-1750162675963.jpeg',
    link: 'https://www.speedotv.in/',
  },
  {
    id: 3,
    badgeKey: 'ad',
    badge: 'Ad',
    tagline: 'Save Our Planet',
    title: 'Discover Earth: Protect Nature & Wildlife Documentary Special',
    description: 'Explore breathtaking landscapes and biodiversity in pristine 4K Ultra HD.',
    ctaKey: 'learnMore',
    cta: 'Learn More',
    accentColor: '#2BA640',
    bgImage: (process.env.PUBLIC_URL || '') + '/img/ad_banner-1748089564748.jpeg',
    link: '',
  },
  {
    id: 4,
    badgeKey: 'sponsored',
    badge: 'Sponsored',
    tagline: 'Speedo Studio',
    title: 'Create, Upload & Monetize High-Speed Video Content Today',
    description: 'Empowering creators worldwide with ultra-fast analytics, monetization, and AI tools.',
    ctaKey: 'getStarted',
    cta: 'Get Started',
    accentColor: '#E65100',
    bgImage: (process.env.PUBLIC_URL || '') + '/img/ad_banner-1749640406432.jpeg',
    link: '/studio',
  },
  {
    id: 5,
    badgeKey: 'ad',
    badge: 'Ad',
    tagline: 'Speedo Premium',
    title: 'Experience Unlimited 4K Ad-Free Streaming & Background Play',
    description: 'Download your favorite videos for offline viewing with zero commercial interruptions.',
    ctaKey: 'tryFree',
    cta: 'Try Free for 30 Days',
    accentColor: '#FF0000',
    bgImage: (process.env.PUBLIC_URL || '') + '/img/banner.png',
    link: '',
  },
];

export default function BannerCarousel({ onNavigate }) {
  const [cards, setCards] = useState(DEFAULT_PROMO_CARDS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [, setLangTick] = useState(0);

  // Fetch active ad banners from CRM backend
  const fetchBanners = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/ad_banners?type=home`, {
        headers: {
          token: 'c7e993808a344da9d59cfa20fa8b6061',
          devicetype: '0',
          request_type: 'USER',
        },
      });
      if (res.ok) {
        const json = await res.json();
        if (json?.status === 200 && Array.isArray(json?.data) && json.data.length > 0) {
          const liveCards = json.data.map((item, idx) => {
            const rawUrl = item.ad_banner_url || '';
            const cleanUrl = rawUrl.replace(/([^:])\/\/+/g, '$1/');
            const accent = ACCENT_COLORS[idx % ACCENT_COLORS.length];
            return {
              id: item.id || `crm_banner_${idx}`,
              badgeKey: 'ad',
              badge: 'Ad',
              tagline: item.description ? item.title : 'Sponsored Ad',
              title: item.title || 'Featured Promotion',
              description: item.description || '',
              ctaKey: 'learnMore',
              cta: item.link ? 'Learn More' : 'Stream Now',
              accentColor: accent,
              bgImage: normalizeMediaUrl(cleanUrl) || `${process.env.PUBLIC_URL || ''}/img/banner.png`,
              link: item.link || '',
            };
          });
          setCards(liveCards);
          return;
        }
      }
    } catch (err) {
      console.warn('Could not load CRM ad banners, using fallback promo cards:', err.message);
    }
    setCards(DEFAULT_PROMO_CARDS);
  }, []);

  useEffect(() => {
    fetchBanners();

    // Re-fetch banners when updated or when returning to this browser tab
    const handleRefresh = () => fetchBanners();
    window.addEventListener('speedotube_banners_updated', handleRefresh);
    window.addEventListener('focus', handleRefresh);

    return () => {
      window.removeEventListener('speedotube_banners_updated', handleRefresh);
      window.removeEventListener('focus', handleRefresh);
    };
  }, [fetchBanners]);

  useEffect(() => {
    const handleLangChange = () => setLangTick((t) => t + 1);
    window.addEventListener('speedotube_language_changed', handleLangChange);
    return () => window.removeEventListener('speedotube_language_changed', handleLangChange);
  }, []);

  // Auto-rotation every 5s if more than 1 banner
  useEffect(() => {
    if (isHovered || cards.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % cards.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [isHovered, cards.length]);

  // Keep index within bounds if cards length changes
  useEffect(() => {
    if (currentIndex >= cards.length) {
      setCurrentIndex(0);
    }
  }, [cards.length, currentIndex]);

  const handlePrev = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? cards.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % cards.length);
  };

  const handleBannerAction = (card) => {
    if (!card?.link) return;
    const url = card.link.trim();
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else if (url.startsWith('/')) {
      if (onNavigate) {
        onNavigate(url);
      } else {
        window.history.pushState({}, '', url);
        window.dispatchEvent(
          new CustomEvent('speedotube_navigate', {
            detail: { path: url },
          })
        );
      }
    }
  };

  if (!cards || cards.length === 0) return null;

  return (
    <div
      className="yt-banner-carousel"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="yt-banner-card">
        {/* Stacked Fade Out / Fade In Slides Container */}
        <div className="yt-banner-fade-container">
          {cards.map((card, idx) => (
            <div
              key={card.id}
              className={`yt-banner-slide ${idx === currentIndex ? 'active' : ''}`}
              onClick={() => card.link && handleBannerAction(card)}
              style={{ cursor: card.link ? 'pointer' : 'default' }}
            >
              <img
                src={card.bgImage}
                alt={card.title}
                className="yt-banner-bg"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
                }}
              />
              <div className="yt-banner-vignette-overlay" />

              <div className="yt-banner-content">
                <div className="yt-banner-badge-row">
                  <span
                    className="yt-banner-badge"
                    style={{ backgroundColor: card.accentColor }}
                  >
                    {getTranslation(card.badgeKey) || card.badge}
                  </span>
                  <span className="yt-banner-tagline">{card.tagline}</span>
                </div>

                <h2 className="yt-banner-title">{card.title}</h2>
                <p className="yt-banner-desc">{card.description}</p>

                <button
                  type="button"
                  className="yt-banner-cta-btn"
                  style={{ backgroundColor: card.accentColor }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBannerAction(card);
                  }}
                >
                  {getTranslation(card.ctaKey) || card.cta}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows (rendered if more than 1 banner) */}
        {cards.length > 1 && (
          <>
            <button
              type="button"
              className="yt-carousel-nav-btn prev"
              onClick={handlePrev}
              title="Previous Banner"
              aria-label="Previous Banner"
            >
              <MdChevronLeft size={28} />
            </button>

            <button
              type="button"
              className="yt-carousel-nav-btn next"
              onClick={handleNext}
              title="Next Banner"
              aria-label="Next Banner"
            >
              <MdChevronRight size={28} />
            </button>

            {/* Navigation Dots */}
            <div className="yt-carousel-dots">
              {cards.map((card, idx) => (
                <button
                  key={card.id}
                  type="button"
                  className={`yt-carousel-dot ${idx === currentIndex ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(idx);
                  }}
                  title={`Go to slide ${idx + 1}`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
